/**
 * Send-to-Telegram from a file-list row (.tg-rail icon).
 *
 * POST /api/files/telegram-send {href} — server re-checks access (incl. OT) and
 * delivers via @rons_org_library_bot. Icon-local feedback: telegram -> spinner ->
 * check (3s) / cross; 409 tg_not_bound turns the icon into a Settings shortcut.
 *
 * Cancellation: while sending, hovering the spinner swaps it to an X; clicking
 * aborts the in-flight upload server-side (POST /api/files/telegram-send-cancel
 * {href} — the streaming upload stops mid-transfer, the file is NOT delivered).
 *
 * Oversize (>50 MB = Bot API hard cap for documents): rows still send — the
 * server delivers a MESSAGE with a download-link button instead of the file
 * (response mode:'link'). data-tg-oversize (set in render.js) only switches
 * the tooltips/labels so the user knows a link is coming.
 *
 * Touch devices: long-press on a row opens a small ACTION SHEET (send via bot /
 * native share / cancel) — it never sends instantly (that was the pre-2026-07-19
 * behavior and it surprised users). "Share…" uses navigator.share(url) = the
 * standard OS sheet.
 *
 * Shared i18n keys: search_tg_* (pages/search/translations.json).
 */

function tr(key, fallback) {
	try {
		const lang = window.LANG || 'EN';
		const text = window.TranslationRegistry && window.TranslationRegistry.translate(key, lang);
		return (text && text !== key) ? text : fallback;
	} catch (e) { return fallback; }
}

export function tgSendTooltip() {
	return tr('search_tg_send_tooltip', 'Send the file to Telegram');
}

export function tgOversizeTooltip() {
	return tr('search_tg_send_too_large', 'File is too large for Telegram (50 MB limit)');
}

export function tgLinkTooltip() {
	return tr('search_tg_send_link_tooltip', 'File is over 50 MB (Telegram limit) — a download link will be sent to the chat');
}

// Map a failed send to a SPECIFIC user-facing message — never a generic-only or
// silent failure. 429 rate-limit, forbidden, not-found each get their own wording.
function tgErrorMessage(status, errorCode) {
	if (status === 429 || errorCode === 'throttled')
		return tr('search_tg_send_throttled', "You're sending files too fast — wait a moment and try again");
	if (errorCode === 'too_large')
		return tr('search_tg_send_too_large', 'File is too large for Telegram (50 MB limit)');
	if (errorCode === 'forbidden')
		return tr('search_tg_send_forbidden', "You don't have access to this file");
	if (errorCode === 'not_found')
		return tr('search_tg_send_not_found', 'File not found');
	return tr('search_tg_send_error', 'Failed to send. Please try again later.');
}

function setIconOf($icon, cls) {
	$icon.attr('class', 'bi tg-send-icon ' + cls);
}

// Visible transient message (the icon-only `title` was invisible on touch and on
// colored rows). Fixed bottom-center, high-contrast, auto-dismiss. One at a time.
let _tgToastTimer = null;
export function showTgToast(message, kind) {
	let $t = $('.tg-toast');
	if (!$t.length) {
		$t = $('<div class="tg-toast" role="status" aria-live="polite"></div>');
		$('body').append($t);
	}
	$t.attr('class', 'tg-toast tg-toast--' + (kind || 'info')).text(message);
	void $t[0].offsetWidth; // reflow so the enter transition restarts
	$t.addClass('is-visible');
	if (_tgToastTimer) clearTimeout(_tgToastTimer);
	_tgToastTimer = setTimeout(() => { $t.removeClass('is-visible'); }, 4200);
}

function cancelSend($icon, href) {
	// Server-side abort: the streaming upload checks the flag between chunks.
	fetch('/api/files/telegram-send-cancel', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'same-origin',
		body: JSON.stringify({ href: href })
	}).catch(() => {});
	const ctrl = $icon.data('tg-abort');
	if (ctrl) { try { ctrl.abort(); } catch (e) {} }
	setIconOf($icon, 'bi-telegram');
	$icon.attr('title', tr('search_tg_send_cancelled', 'Sending cancelled'))
		.removeData('tg-busy').removeData('tg-abort');
	setTimeout(() => { $icon.attr('title', tgSendTooltip()); }, 2500);
}

export function sendFileToTelegram($icon, href) {
	if ($icon.data('tg-busy')) return;
	$icon.data('tg-busy', true);
	const setIcon = (cls) => setIconOf($icon, cls);
	setIcon('bi-arrow-repeat tg-send-spin');
	$icon.attr('title', tr('search_tg_send_cancel', 'Click to cancel sending'));

	const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
	if (ctrl) $icon.data('tg-abort', ctrl);

	fetch('/api/files/telegram-send', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'same-origin',
		body: JSON.stringify({ href: href }),
		signal: ctrl ? ctrl.signal : undefined
	}).then(async (res) => {
		const data = await res.json().catch(() => ({}));
		$icon.removeData('tg-abort');
		if (res.ok && data.success) {
			const isLinkMode = data.mode === 'link' || (data.data && data.data.mode === 'link');
			setIcon('bi-check-lg tg-send-ok');
			const okMsg = isLinkMode
				? tr('search_tg_send_link_sent', 'Link sent to Telegram — the file is over 50 MB')
				: tr('search_tg_send_sent', 'Sent to Telegram — check the bot chat');
			$icon.attr('title', okMsg);
			showTgToast(okMsg, 'ok');
			setTimeout(() => { setIcon('bi-telegram'); $icon.attr('title', tgSendTooltip()); $icon.removeData('tg-busy'); }, 3000);
			return;
		}
		if (data.error === 'cancelled') {
			// Cancel already rendered its feedback in cancelSend().
			$icon.removeData('tg-busy');
			return;
		}
		if (res.status === 409) {
			// Not bound: ONE click. The old flow set a red triangle (invisible on
			// colored rows) with a title-only hint and needed a SECOND click to
			// reach Settings. Now: reset the icon, show a visible toast, and open
			// Settings straight at the Telegram section.
			setIcon('bi-telegram');
			$icon.attr('title', tgSendTooltip()).removeData('tg-busy');
			showTgToast(tr('search_tg_send_not_bound_nav', 'Telegram is not linked — opening Settings to connect it'), 'warn');
			setTimeout(() => { window.location.href = '/settings#telegram'; }, 1300);
			return;
		}
		const errMsg = tgErrorMessage(res.status, data.error);
		setIcon('bi-x-lg tg-send-warn');
		$icon.attr('title', errMsg);
		showTgToast(errMsg, 'warn');
		setTimeout(() => { setIcon('bi-telegram'); $icon.attr('title', tgSendTooltip()); $icon.removeData('tg-busy'); }, 4000);
	}).catch((err) => {
		$icon.removeData('tg-abort');
		if (err && err.name === 'AbortError') { $icon.removeData('tg-busy'); return; }
		const netMsg = tr('search_tg_send_error', 'Failed to send. Please try again later.');
		setIcon('bi-x-lg tg-send-warn');
		$icon.attr('title', netMsg);
		showTgToast(netMsg, 'warn');
		setTimeout(() => { setIcon('bi-telegram'); $icon.attr('title', tgSendTooltip()); $icon.removeData('tg-busy'); }, 4000);
	});
}

const LONG_PRESS_MS = 550;
const MOVE_CANCEL_PX = 12;

function isTouchLike() {
	try { return window.matchMedia('(hover: none), (pointer: coarse)').matches; } catch (e) { return false; }
}

function showLongtapIndicator($item, stateCls) {
	let $ind = $item.find('.tg-longtap-indicator');
	if (!$ind.length) {
		$ind = $('<div class="tg-longtap-indicator"><i class="bi bi-telegram"></i></div>');
		$item.append($ind);
	}
	$ind.find('i').attr('class', 'bi ' + stateCls);
	return $ind;
}

function longtapSend($item, href) {
	try { if (navigator.vibrate) navigator.vibrate(30); } catch (e) {}
	const $ind = showLongtapIndicator($item, 'bi-arrow-repeat tg-send-spin');
	// Tap on the indicator while spinning = cancel (no hover on touch).
	$ind.css('pointer-events', 'auto').off('click.tgCancel').on('click.tgCancel', function(e) {
		e.preventDefault();
		e.stopPropagation();
		fetch('/api/files/telegram-send-cancel', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'same-origin',
			body: JSON.stringify({ href: href })
		}).catch(() => {});
		$item.find('.tg-longtap-indicator').remove();
	});
	const done = (cls, keepMs) => {
		showLongtapIndicator($item, cls).css('pointer-events', 'none').off('click.tgCancel');
		setTimeout(() => { $item.find('.tg-longtap-indicator').remove(); }, keepMs);
	};
	fetch('/api/files/telegram-send', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'same-origin',
		body: JSON.stringify({ href: href })
	}).then(async (res) => {
		const data = await res.json().catch(() => ({}));
		if (res.ok && data.success) {
			const isLinkMode = data.mode === 'link' || (data.data && data.data.mode === 'link');
			done('bi-check-lg', 2500);
			showTgToast(isLinkMode
				? tr('search_tg_send_link_sent', 'Link sent to Telegram — the file is over 50 MB')
				: tr('search_tg_send_sent', 'Sent to Telegram — check the bot chat'), 'ok');
			return;
		}
		if (data.error === 'cancelled') { $item.find('.tg-longtap-indicator').remove(); return; }
		if (res.status === 409) {
			$item.find('.tg-longtap-indicator').remove();
			showTgToast(tr('search_tg_send_not_bound_nav', 'Telegram is not linked — opening Settings to connect it'), 'warn');
			setTimeout(() => { window.location.href = '/settings#telegram'; }, 1300);
			return;
		}
		done('bi-x-lg tg-send-warn', 2500);
		showTgToast(tgErrorMessage(res.status, data.error), 'warn');
	}).catch(() => {
		done('bi-x-lg tg-send-warn', 2500);
		showTgToast(tr('search_tg_send_error', 'Failed to send. Please try again later.'), 'warn');
	});
}

// ---- Long-press ACTION SHEET (touch devices) ----

// iOS re-targets sticky :hover whenever the element it sat on leaves the screen
// (our sheet overlay removal, but ALSO the OS share-sheet dismissal): WebKit
// re-resolves hover at the last touch point — and the file row that sits UNDER
// the tapped button lights up as if hovered. Blur any focused row link and take
// rows out of hit-testing for the recompute window so the re-resolution lands
// on the container. No event exists for the recompute — hence the timed window.
function guardStickyHoverRetarget() {
	try {
		if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
	} catch (e) {}
	if (!isTouchLike()) return;
	const list = document.getElementById('file-list-container');
	if (!list) return;
	list.style.pointerEvents = 'none';
	setTimeout(() => { list.style.pointerEvents = ''; }, 400);
}

function closeTgActionSheet() {
	const hadSheet = $('.tg-action-sheet-overlay').length > 0;
	$('.tg-action-sheet-overlay').remove();
	if (hadSheet) guardStickyHoverRetarget();
}

function openTgActionSheet($item) {
	const $link = $item.find('.file-link');
	const href = $link.attr('href');
	if (!href || !href.startsWith('/library/')) return;
	const fileName = ($item.find('.file-name').text() || '').trim();
	const oversize = $item.find('.tg-send-icon').data('tg-oversize') === 1
		|| $item.find('.tg-send-icon').attr('data-tg-oversize') === '1';
	const canShare = !!navigator.share;

	try { if (navigator.vibrate) navigator.vibrate(20); } catch (e) {}
	// The OS selection gesture races our 550ms timer — drop anything it grabbed.
	try { const sel = window.getSelection(); if (sel) sel.removeAllRanges(); } catch (e) {}
	closeTgActionSheet();

	const sendLabel = tr('search_tg_menu_send', 'Send to Telegram');
	const shareLabel = tr('search_tg_menu_share', 'Share…');
	const cancelLabel = tr('search_tg_menu_cancel', 'Cancel');

	const $overlay = $('<div class="tg-action-sheet-overlay"></div>');
	const $sheet = $('<div class="tg-action-sheet" role="menu"></div>');
	$sheet.append($('<div class="tg-action-sheet__title"></div>').text(fileName));

	const $send = $('<button type="button" class="tg-action-sheet__btn"><i class="bi bi-telegram"></i> <span></span></button>');
	// Oversize: same working action, honest label — the bot sends a link message.
	$send.find('span').text(oversize
		? tr('search_tg_menu_send_link', 'Send a link (file over 50 MB)')
		: sendLabel);
	$send.on('click', function() {
		closeTgActionSheet();
		longtapSend($item, href);
	});
	$sheet.append($send);

	// Favorites toggle (rendered only when the row carries a fav icon = authenticated).
	const $favIcon = $item.find('.fav-icon');
	const favHref = $favIcon.attr('data-fav-href');
	const favPath = $favIcon.attr('data-fav-path');
	if (favHref && favPath) {
		const $fav = $('<button type="button" class="tg-action-sheet__btn"><i class="bi"></i> <span></span></button>');
		import('../../favorites/favorites-state.js').then((fav) => {
			const on = fav.favHas(favPath);
			$fav.find('i').addClass(on ? 'bi-star-fill' : 'bi-star');
			$fav.find('span').text(on
				? tr('fav_menu_remove', 'Remove from favorites')
				: tr('fav_menu_add', 'Add to favorites'));
			$fav.on('click', function() {
				closeTgActionSheet();
				fav.favToggle(favHref, favPath);
			});
		}).catch(() => { $fav.remove(); });
		$sheet.append($fav);
	}

	if (canShare) {
		const $share = $('<button type="button" class="tg-action-sheet__btn"><i class="bi bi-box-arrow-up"></i> <span></span></button>');
		$share.find('span').text(shareLabel);
		$share.on('click', function() {
			closeTgActionSheet();
			try {
				// The promise settles when the OS share sheet closes — WebKit
				// re-resolves sticky hover again at that moment, so guard again.
				navigator.share({ title: fileName, url: window.location.origin + href })
					.then(guardStickyHoverRetarget, guardStickyHoverRetarget);
			} catch (e) {}
		});
		$sheet.append($share);
	}

	const $cancel = $('<button type="button" class="tg-action-sheet__btn tg-action-sheet__btn--cancel"><span></span></button>');
	$cancel.find('span').text(cancelLabel);
	$cancel.on('click', closeTgActionSheet);
	$sheet.append($cancel);

	$overlay.on('click', function(e) { if (e.target === this) closeTgActionSheet(); });
	$overlay.append($sheet);
	$('body').append($overlay);
}

/**
 * Touch devices: long-press on a file row opens the action sheet (send via bot /
 * native share / cancel) — never an instant send.
 * Cancelled by scroll/move (>12px) or early release; suppresses the context menu
 * and the trailing click only when the long-press actually fired.
 */
export function initTgLongTapEvents(container) {
	if (!isTouchLike()) return;
	let timer = null;
	let startXY = null;
	let fired = false;
	let $target = null;

	const cancel = () => { if (timer) { clearTimeout(timer); timer = null; } startXY = null; $target = null; };

	container.off('touchstart.tgLongtap', '.file-item')
		.on('touchstart.tgLongtap', '.file-item', function(e) {
			const t = e.originalEvent.touches && e.originalEvent.touches[0];
			if (!t) return;
			fired = false;
			startXY = { x: t.clientX, y: t.clientY };
			$target = $(this);
			timer = setTimeout(() => {
				fired = true;
				timer = null;
				if ($target) openTgActionSheet($target);
			}, LONG_PRESS_MS);
		})
		.off('touchmove.tgLongtap', '.file-item')
		.on('touchmove.tgLongtap', '.file-item', function(e) {
			if (!startXY) return;
			const t = e.originalEvent.touches && e.originalEvent.touches[0];
			if (!t) return;
			if (Math.abs(t.clientX - startXY.x) > MOVE_CANCEL_PX ||
			    Math.abs(t.clientY - startXY.y) > MOVE_CANCEL_PX) {
				cancel();
			}
		})
		.off('touchend.tgLongtap touchcancel.tgLongtap', '.file-item')
		.on('touchend.tgLongtap touchcancel.tgLongtap', '.file-item', function(e) {
			if (fired) {
				// Long-press consumed the gesture: no navigation, no context menu.
				e.preventDefault();
			}
			cancel();
		})
		.off('contextmenu.tgLongtap', '.file-item')
		.on('contextmenu.tgLongtap', '.file-item', function(e) {
			// Android: rows are links, and Chrome opens the NATIVE link context
			// menu at ~500ms — before our 550ms timer — stealing the gesture
			// (our sheet never appears). These handlers are bound only on touch
			// devices (isTouchLike guard), so suppress it unconditionally; iOS
			// needs the CSS -webkit-touch-callout:none instead (rails.css).
			e.preventDefault();
		})
		// Android starts text selection at ~500ms — BEFORE our 550ms timer. While a
		// press is in progress on a row, the gesture belongs to us: block selection.
		.off('selectstart.tgLongtap', '.file-item')
		.on('selectstart.tgLongtap', '.file-item', function(e) {
			if (startXY || fired) { e.preventDefault(); }
		});

	// Swallow the synthetic click that follows a consumed long-press.
	container.off('click.tgLongtapGuard', '.file-item')
		.on('click.tgLongtapGuard', '.file-item', function(e) {
			if (fired) {
				e.preventDefault();
				e.stopImmediatePropagation();
				fired = false;
			}
		});
}

export function initTgSendEvents(container) {
	container.off('click.tgSend', '.tg-rail')
		.on('click.tgSend', '.tg-rail', function(e) {
			// The row itself is a click-to-open control — never let this bubble.
			e.preventDefault();
			e.stopPropagation();
			const $icon = $(this).find('.tg-send-icon');
			if (!$icon.length) return;
			const href = $icon.attr('data-tg-href');
			if (!href) return;
			if ($icon.data('tg-busy')) {
				// Second click while sending = cancel.
				cancelSend($icon, href);
				return;
			}
			// Oversize files send too — the server delivers a link message instead
			// of the document (data-tg-oversize only varies the tooltips).
			sendFileToTelegram($icon, href);
		});

	// Cancel affordance: hovering the spinner swaps it to an X.
	container.off('mouseenter.tgSendHover', '.tg-send-icon')
		.on('mouseenter.tgSendHover', '.tg-send-icon', function() {
			const $icon = $(this);
			if ($icon.data('tg-busy') && $icon.hasClass('tg-send-spin')) {
				setIconOf($icon, 'bi-x-lg tg-send-cancelhint');
			}
		})
		.off('mouseleave.tgSendHover', '.tg-send-icon')
		.on('mouseleave.tgSendHover', '.tg-send-icon', function() {
			const $icon = $(this);
			if ($icon.data('tg-busy') && $icon.hasClass('tg-send-cancelhint')) {
				setIconOf($icon, 'bi-arrow-repeat tg-send-spin');
			}
		});
}
